import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { CONTACT } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' }); // idle | loading | sent | error
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.subject.trim()) e.subject = 'Subject is required';
    if (!form.message.trim()) e.message = 'Message is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus({ state: 'loading', msg: '' });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: 'sent', msg: "Thanks! Your message has been sent. We'll reply within 24 hours." });
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus({ state: 'idle', msg: '' }), 6000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setStatus({ state: 'error', msg: typeof msg === 'string' ? msg : 'Please check your inputs.' });
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#f6f9fc]">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="eyebrow mb-3">GET IN TOUCH</p>
          <h2 className="section-title text-4xl md:text-5xl">Let&apos;s Start a Conversation</h2>
          <p className="text-[#556a85] text-lg mt-4 max-w-2xl mx-auto">
            Whether you have a question about our services or want to start your growth journey, our team is
            ready to answer any questions.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 bg-[#0a2c4d] rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-[#0a6cb0]/30 blur-2xl" />
            <div className="relative">
              <h3 className="font-serif-display text-2xl font-bold mb-2">Contact Information</h3>
              <p className="text-white/70 text-sm mb-8">Reach out through any channel below.</p>

              <div className="space-y-5">
                <InfoRow icon={Mail} label="Email" value={CONTACT.email} />
                <InfoRow icon={Phone} label="Phone" value={CONTACT.phone} />
                <InfoRow icon={MapPin} label="Address" value={CONTACT.address} />
                <InfoRow icon={Clock} label="Working Hours" value={CONTACT.hours} />
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl p-8 shadow-xl border border-[#eef1f5]">
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <Field label="Full Name" error={errors.name}>
                <input type="text" value={form.name} onChange={update('name')} placeholder="John Smith" className={inputCls(errors.name)} />
              </Field>
              <Field label="Email Address" error={errors.email}>
                <input type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" className={inputCls(errors.email)} />
              </Field>
            </div>
            <div className="mb-5">
              <Field label="Subject" error={errors.subject}>
                <input type="text" value={form.subject} onChange={update('subject')} placeholder="How can we help?" className={inputCls(errors.subject)} />
              </Field>
            </div>
            <div className="mb-6">
              <Field label="Message" error={errors.message}>
                <textarea rows={6} value={form.message} onChange={update('message')} placeholder="Tell us about your project or question..." className={inputCls(errors.message)} />
              </Field>
            </div>
            <button
              type="submit"
              disabled={status.state === 'loading'}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0a6cb0] text-white font-semibold uppercase tracking-wider text-sm hover:bg-[#0a86d4] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status.state === 'loading' ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (<>Send Message <Send size={16} /></>)}
            </button>

            {status.state === 'sent' && (
              <div className="mt-5 flex items-center gap-2 text-[#0a7a4d] bg-[#e6f5ed] px-4 py-3 rounded-lg text-sm">
                <CheckCircle2 size={18} />
                <span>{status.msg}</span>
              </div>
            )}
            {status.state === 'error' && (
              <div className="mt-5 flex items-center gap-2 text-[#a83232] bg-[#fdecec] px-4 py-3 rounded-lg text-sm">
                <AlertCircle size={18} />
                <span>{status.msg}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
      <Icon size={16} />
    </div>
    <div>
      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-[15px]">{value}</p>
    </div>
  </div>
);

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="block text-[#0a2c4d] text-sm font-semibold mb-2">{label}</span>
    {children}
    {error && <span className="block text-red-500 text-xs mt-1">{error}</span>}
  </label>
);

const inputCls = (err) =>
  `w-full px-4 py-3 rounded-lg border bg-white text-[#0a2c4d] placeholder-[#a2b3c8] text-sm focus:outline-none focus:ring-2 focus:ring-[#0a6cb0]/30 transition-colors ${
    err ? 'border-red-300' : 'border-[#dbe4ee] focus:border-[#0a6cb0]'
  }`;

export default ContactSection;
